import { useEffect, useState } from 'react';
import {
  MousePointer, Circle, Square, Minus, Trash2, RotateCcw, Save, FolderOpen,
  Download, Upload, FileUp, Ruler, Check, X, RotateCcwSquare,
  RotateCwSquare, Droplet, Pencil, Network, Shapes, Menu, Truck, Smartphone, Share,
  Undo2, MapPinned, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription
} from './ui/drawer';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from './ui/dropdown-menu';
import { cn, formatDistance } from '../lib/utils';
import { canPromptInstall, isIOS, isStandalone, promptInstall, subscribeInstall } from '../lib/pwa';
import { POI_CATEGORIES, POI_MIN_ZOOM } from '../lib/poiCategories';
import { ARROW_TYPES, isPathTool } from '../lib/lineStyles';
import { ArrowToolIcon, HoseToolIcon } from './ToolIcons';

const DRAW_TOOLS = [
  { id: 'circle', icon: Circle, label: 'Kreis' },
  { id: 'polygon', icon: Square, label: 'Fläche' },
  { id: 'polyline', icon: Minus, label: 'Linie' },
  { id: 'arrow', icon: ArrowToolIcon, label: 'Richtungspfeil' },
  { id: 'hose', icon: HoseToolIcon, label: 'Schlauchleitung' },
];

const NavButton = ({ icon: Icon, label, active, disabled, onClick, testId, ...props }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={cn(
      "flex-1 min-w-0 max-w-[7.5rem] flex flex-col items-center justify-center gap-0.5 h-14 rounded-md text-[11px] font-medium transition-colors",
      "disabled:opacity-40 active:bg-lunefire-red/10",
      active ? "text-lunefire-red" : "text-muted-foreground"
    )}
    data-testid={testId}
    {...props}
  >
    <Icon className="w-5 h-5" />
    <span className="truncate max-w-full">{label}</span>
  </button>
);

const MenuItem = ({ icon: Icon, label, onClick, disabled, active, destructive }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={cn(
      "w-full flex items-center gap-3 px-4 h-12 rounded-md text-left text-sm font-medium",
      "active:bg-lunefire-red/10 disabled:opacity-40",
      destructive && "text-lunefire-red",
      active && "bg-lunefire-red/10 text-lunefire-red"
    )}
  >
    <Icon className="w-5 h-5 shrink-0" />
    <span className="flex-1">{label}</span>
    {active && <Check className="w-4 h-4" />}
  </button>
);

// Aufklappbare Liste der POI-Kategorien im „Mehr“-Panel; Ankreuzen schließt das Panel nicht
const PoiSection = ({ categories, onToggle, onSetAll }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          "w-full flex items-center gap-3 px-4 h-12 rounded-md text-left text-sm font-medium active:bg-lunefire-red/10",
          categories.length > 0 && "text-lunefire-red"
        )}
        aria-expanded={open}
        data-testid="mobile-poi-toggle"
      >
        <MapPinned className="w-5 h-5 shrink-0" />
        <span className="flex-1">
          POIs einblenden{categories.length > 0 ? ` (${categories.length})` : ''}
          <span className="block text-xs font-normal text-muted-foreground">ab Zoomstufe {POI_MIN_ZOOM}</span>
        </span>
        <ChevronDown className={cn("w-4 h-4 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="pl-6 pb-1">
          {POI_CATEGORIES.map(({ key, label, color, Icon }) => {
            const active = categories.includes(key);
            return (
              <button
                key={key}
                type="button"
                onClick={() => onToggle(key)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 h-11 rounded-md text-left text-sm active:bg-lunefire-red/10",
                  active && "bg-lunefire-red/10"
                )}
                aria-pressed={active}
                data-testid={`mobile-poi-${key}`}
              >
                <span className="w-6 h-6 rounded-full flex items-center justify-center shrink-0" style={{ background: color }}>
                  <Icon className="w-3.5 h-3.5 text-white" />
                </span>
                <span className="flex-1">{label}</span>
                {active && <Check className="w-4 h-4 text-lunefire-red" />}
              </button>
            );
          })}
          <div className="flex gap-2 px-3 pt-1">
            <Button size="sm" variant="outline" className="h-10 flex-1" onClick={() => onSetAll(POI_CATEGORIES.map(c => c.key))}>
              Alle
            </Button>
            <Button size="sm" variant="outline" className="h-10 flex-1" disabled={categories.length === 0} onClick={() => onSetAll([])}>
              Keine
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

const useInstallState = () => {
  const [canPrompt, setCanPrompt] = useState(canPromptInstall);
  useEffect(() => subscribeInstall(() => setCanPrompt(canPromptInstall())), []);
  return { canPrompt, installable: !isStandalone() && (canPrompt || isIOS()) };
};

export const showIOSInstallHelp = () => {
  toast('App installieren', {
    description: 'Im Browser auf „Teilen“ tippen und „Zum Home-Bildschirm“ wählen.',
    duration: 8000,
  });
};

// Untere Bedienleiste im Handy-Layout, dazu die Panels „Elemente“ und „Mehr“
export const MobileToolbar = ({
  activeTool,
  onToolChange,
  onAction,
  canUndo,
  hasElements,
  showHydrants,
  onToggleHydrants,
  poiCategories,
  onTogglePoiCategory,
  onSetPoiCategories,
  onOpenFuehrung,
  renderCatalog,
  placingActive,
}) => {
  const [showElements, setShowElements] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const { canPrompt, installable } = useInstallState();

  const activeDrawTool = DRAW_TOOLS.find(t => t.id === activeTool);

  const runAction = (fn) => {
    setShowMore(false);
    fn();
  };

  const install = async () => {
    setShowMore(false);
    if (canPrompt) {
      await promptInstall();
    } else {
      showIOSInstallHelp();
    }
  };

  return (
    <>
      <nav
        className="flex items-stretch justify-center gap-1 px-1 border-t border-lunefire-red/20 bg-card/95 backdrop-blur pb-[env(safe-area-inset-bottom)] pl-[max(0.25rem,env(safe-area-inset-left))] pr-[max(0.25rem,env(safe-area-inset-right))]"
        data-testid="mobile-toolbar"
      >
        <NavButton
          icon={Truck}
          label="Elemente"
          active={showElements || placingActive}
          onClick={() => setShowElements(true)}
          testId="mobile-elements-btn"
        />
        <NavButton
          icon={MousePointer}
          label="Auswählen"
          active={activeTool === 'select' && !placingActive}
          onClick={() => onToolChange('select')}
          testId="mobile-tool-select"
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <NavButton
              icon={activeDrawTool?.icon || Shapes}
              label={activeDrawTool?.label || 'Zeichnen'}
              active={Boolean(activeDrawTool)}
              testId="mobile-draw-btn"
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="center" className="w-48">
            {DRAW_TOOLS.map(tool => (
              <DropdownMenuItem
                key={tool.id}
                onSelect={() => onToolChange(tool.id)}
                className={cn("h-11 gap-3", activeTool === tool.id && "text-lunefire-red")}
              >
                <tool.icon className="w-4 h-4" />
                {tool.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <NavButton
          icon={Ruler}
          label="Messen"
          active={activeTool === 'measure'}
          onClick={() => onToolChange(activeTool === 'measure' ? 'select' : 'measure')}
          testId="mobile-tool-measure"
        />
        <NavButton
          icon={RotateCcw}
          label="Zurück"
          disabled={!canUndo}
          onClick={() => onAction('undo')}
          testId="mobile-undo-btn"
        />
        <NavButton
          icon={Menu}
          label="Mehr"
          active={showMore}
          onClick={() => setShowMore(true)}
          testId="mobile-more-btn"
        />
      </nav>

      <Drawer open={showElements} onOpenChange={setShowElements} shouldScaleBackground={false}>
        <DrawerContent className="max-h-[85dvh] sm:mx-auto sm:max-w-xl" data-testid="mobile-elements-panel">
          <DrawerHeader className="pb-1 text-left">
            <DrawerTitle className="font-display uppercase tracking-wider text-sm">Elemente</DrawerTitle>
            <DrawerDescription>Antippen, dann auf die Karte tippen, um es zu platzieren.</DrawerDescription>
          </DrawerHeader>
          <div className="overflow-y-auto pb-[env(safe-area-inset-bottom)]">
            {renderCatalog(() => setShowElements(false))}
          </div>
        </DrawerContent>
      </Drawer>

      <Drawer open={showMore} onOpenChange={setShowMore} shouldScaleBackground={false}>
        <DrawerContent className="max-h-[85dvh] sm:mx-auto sm:max-w-xl" data-testid="mobile-more-panel">
          <DrawerHeader className="pb-1 text-left">
            <DrawerTitle className="font-display uppercase tracking-wider text-sm">Mehr</DrawerTitle>
            <DrawerDescription className="sr-only">Weitere Funktionen</DrawerDescription>
          </DrawerHeader>
          <div className="overflow-y-auto px-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))]">
            <MenuItem icon={Network} label="Führungsstruktur und Funkskizze" onClick={() => runAction(onOpenFuehrung)} />
            <MenuItem icon={Droplet} label="Hydranten anzeigen" active={showHydrants} onClick={() => runAction(onToggleHydrants)} />
            <PoiSection categories={poiCategories} onToggle={onTogglePoiCategory} onSetAll={onSetPoiCategories} />
            <div className="my-1 border-t border-border" />
            <MenuItem icon={Save} label="Szenario speichern" onClick={() => runAction(() => onAction('save'))} />
            <MenuItem icon={FolderOpen} label="Szenario laden" onClick={() => runAction(() => onAction('load'))} />
            <MenuItem icon={Download} label="Szenario exportieren (JSON)" onClick={() => runAction(() => onAction('export'))} />
            <MenuItem icon={FileUp} label="Szenario importieren (JSON)" onClick={() => runAction(() => onAction('import-json'))} />
            <MenuItem icon={Upload} label="GPX importieren" onClick={() => runAction(() => onAction('import-gpx'))} />
            <div className="my-1 border-t border-border" />
            <MenuItem icon={Trash2} label="Alle Elemente löschen" destructive disabled={!hasElements} onClick={() => runAction(() => onAction('clear'))} />
            {installable && (
              <>
                <div className="my-1 border-t border-border" />
                <MenuItem icon={canPrompt ? Smartphone : Share} label="Als App installieren" onClick={install} />
              </>
            )}
            <p className="pt-3 text-[10px] text-muted-foreground text-center">created by freakms</p>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
};

// Kontextleiste über der Bedienleiste: Zeichnen abschließen, Messwert, ausgewähltes Element
export const MobileContextBar = ({
  activeTool,
  drawingPointsCount,
  measurePointsCount,
  measureDistance,
  onFinishDrawing,
  onCancelDrawing,
  onRemoveLastPoint,
  isTouch,
  arrowType,
  onArrowTypeChange,
  selectedElement,
  onDeleteSelected,
  canRotateSelected,
  onRotateSelected,
  canEditSelected,
  onEditSelected,
}) => {
  let content = null;

  const removeLastButton = (count) => (
    <Button size="icon" variant="ghost" className="h-10 w-9 shrink-0" onClick={onRemoveLastPoint} disabled={count === 0} aria-label="Letzten Punkt entfernen">
      <Undo2 className="w-4 h-4" />
    </Button>
  );

  if (isPathTool(activeTool)) {
    const min = activeTool === 'polygon' ? 3 : 2;
    content = (
      <>
        {activeTool === 'arrow' ? (
          // Pfeilart vor oder während des Zeichnens wählen
          <select
            value={arrowType}
            onChange={(e) => onArrowTypeChange(e.target.value)}
            className="flex-1 min-w-0 h-10 rounded-md border border-input bg-background px-2 text-sm"
            aria-label="Pfeilart"
            data-testid="mobile-arrow-type"
          >
            {Object.entries(ARROW_TYPES).map(([key, type]) => (
              <option key={key} value={key}>{type.label}</option>
            ))}
          </select>
        ) : (
          <span className="flex-1 min-w-0 truncate text-sm">
            {drawingPointsCount > 0 ? `${drawingPointsCount} Punkte` : (
              <span className="text-muted-foreground">Punkte auf die Karte tippen</span>
            )}
          </span>
        )}
        {removeLastButton(drawingPointsCount)}
        <Button size="icon" variant="ghost" className="h-10 w-9 shrink-0" onClick={onCancelDrawing} disabled={drawingPointsCount === 0} aria-label="Abbrechen">
          <X className="w-4 h-4" />
        </Button>
        <Button size="sm" className="h-10 px-2.5 bg-green-600 hover:bg-green-700 text-white" onClick={onFinishDrawing} disabled={drawingPointsCount < min}>
          <Check className="w-4 h-4 mr-1" /> Fertig
        </Button>
      </>
    );
  } else if (activeTool === 'measure') {
    content = (
      <>
        <Ruler className="w-4 h-4 shrink-0 text-lunefire-orange" />
        <span className="flex-1 min-w-0 truncate text-sm font-mono font-semibold" data-testid="mobile-measure-value">
          {measureDistance > 0 ? formatDistance(measureDistance) : (
            <span className="font-sans font-normal text-muted-foreground">
              Punkte auf die Karte tippen
            </span>
          )}
        </span>
        {measurePointsCount > 0 && removeLastButton(measurePointsCount)}
        {measurePointsCount > 0 && (
          <Button size="icon" variant="ghost" className="h-10 w-9 shrink-0" onClick={onCancelDrawing} aria-label="Messung löschen">
            <X className="w-4 h-4" />
          </Button>
        )}
      </>
    );
  } else if (activeTool === 'circle') {
    content = (
      <span className="flex-1 text-sm text-muted-foreground py-2">
        Auf die Karte tippen für den Kreismittelpunkt
      </span>
    );
  } else if (selectedElement) {
    content = (
      <>
        <span className="flex-1 min-w-0 text-sm leading-tight text-muted-foreground">
          Ausgewählt
          {isTouch && <span className="block text-xs">Zum Verschieben ziehen</span>}
        </span>
        {canEditSelected && (
          <Button size="icon" variant="ghost" className="h-10 w-10" onClick={onEditSelected} aria-label="Bearbeiten">
            <Pencil className="w-4 h-4" />
          </Button>
        )}
        {canRotateSelected && (
          <>
            <Button size="icon" variant="ghost" className="h-10 w-10" onClick={() => onRotateSelected(-15)} aria-label="Gegen den Uhrzeigersinn drehen">
              <RotateCcwSquare className="w-4 h-4" />
            </Button>
            <Button size="icon" variant="ghost" className="h-10 w-10" onClick={() => onRotateSelected(15)} aria-label="Im Uhrzeigersinn drehen">
              <RotateCwSquare className="w-4 h-4" />
            </Button>
          </>
        )}
        <Button size="icon" className="h-10 w-10 shrink-0 bg-lunefire-red hover:bg-lunefire-red/90 text-white" onClick={onDeleteSelected} aria-label="Löschen" data-testid="mobile-delete-selected-btn">
          <Trash2 className="w-4 h-4" />
        </Button>
      </>
    );
  }

  if (!content) return null;

  return (
    <div
      className="pointer-events-auto flex items-center gap-1 rounded-lg border border-border bg-card/95 backdrop-blur pl-3 pr-1 py-1 shadow-lg font-body"
      data-testid="mobile-context-bar"
    >
      {content}
    </div>
  );
};

export default MobileToolbar;
